import { useState, useEffect, useCallback } from 'react'
import {
  Settings,
  Flag,
  Clock,
  Play,
  Loader2,
  CheckCircle,
  XCircle,
  AlertTriangle,
  Save,
  RefreshCw,
  Bell,
  Brain,
  Calculator,
} from 'lucide-react'
import { adminService } from '../../services/adminService'

// ─── Types ─────────────────────────────────────────────────────────────────────

interface SystemSetting {
  category: string
  key: string
  value: unknown
  description?: string
}

interface CronJob {
  id: string
  name: string
  description: string | null
  cronExpression: string
  handler: string
  status: 'running' | 'idle' | 'failed'
  lastRunAt: string | null
  nextRunAt: string | null
  lastError: string | null
  enabled: boolean
}

interface FeatureFlag {
  id: string
  name: string
  description: string | null
  enabled: boolean
  targetRoles: string[]
}

type TabId = 'settings' | 'cron' | 'flags' | 'scoring' | 'notifications' | 'ai'

interface TabItem {
  id: TabId
  label: string
  icon: React.ElementType
}

// ─── Constants ─────────────────────────────────────────────────────────────────

const TABS: TabItem[] = [
  { id: 'settings', label: 'Settings', icon: Settings },
  { id: 'cron', label: 'Cron Jobs', icon: Clock },
  { id: 'flags', label: 'Feature Flags', icon: Flag },
  { id: 'scoring', label: 'Scoring Rules', icon: Calculator },
  { id: 'notifications', label: 'Notifications', icon: Bell },
  { id: 'ai', label: 'AI Config', icon: Brain },
]

// ─── Component ─────────────────────────────────────────────────────────────────

export function AdminSystem() {
  const [activeTab, setActiveTab] = useState<TabId>('settings')

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold text-gray-900 dark:text-white">
          System Manager
        </h1>
        <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
          Manage platform settings, cron jobs, feature flags, and configurations.
        </p>
      </div>

      {/* Tab Navigation */}
      <div className="border-b border-gray-200 dark:border-gray-700">
        <nav className="-mb-px flex space-x-4 overflow-x-auto" aria-label="System tabs">
          {TABS.map((tab) => {
            const Icon = tab.icon
            const isActive = activeTab === tab.id
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-2 whitespace-nowrap border-b-2 px-3 py-3 text-sm font-medium transition-colors ${
                  isActive
                    ? 'border-blue-600 text-blue-600 dark:border-blue-400 dark:text-blue-400'
                    : 'border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300'
                }`}
                aria-current={isActive ? 'page' : undefined}
              >
                <Icon className="h-4 w-4" />
                {tab.label}
              </button>
            )
          })}
        </nav>
      </div>

      {/* Tab Content */}
      <div>
        {activeTab === 'settings' && <SettingsTab />}
        {activeTab === 'cron' && <CronJobsTab />}
        {activeTab === 'flags' && <FeatureFlagsTab />}
        {activeTab === 'scoring' && <ScoringRulesTab />}
        {activeTab === 'notifications' && <NotificationsTab />}
        {activeTab === 'ai' && <AIConfigTab />}
      </div>
    </div>
  )
}

// ─── Settings Tab ──────────────────────────────────────────────────────────────

function SettingsTab() {
  const [settings, setSettings] = useState<SystemSetting[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [editingKey, setEditingKey] = useState<string | null>(null)
  const [editValue, setEditValue] = useState('')
  const [saving, setSaving] = useState(false)
  const [saveSuccess, setSaveSuccess] = useState<string | null>(null)

  const fetchSettings = useCallback(async () => {
    try {
      setLoading(true)
      setError(null)
      const res = await adminService.getSettings()
      const data = res.data?.settings || res.data || []
      setSettings(Array.isArray(data) ? data : [])
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to load settings')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => { fetchSettings() }, [fetchSettings])

  const handleEdit = (setting: SystemSetting) => {
    const compositeKey = `${setting.category}::${setting.key}`
    setEditingKey(compositeKey)
    setEditValue(typeof setting.value === 'string' ? setting.value : JSON.stringify(setting.value, null, 2))
    setSaveSuccess(null)
  }

  const handleSave = async (setting: SystemSetting) => {
    try {
      setSaving(true)
      let parsedValue: unknown = editValue
      try {
        parsedValue = JSON.parse(editValue)
      } catch {
        // Use string value as-is
      }
      await adminService.updateSetting(setting.category, setting.key, parsedValue)
      setSaveSuccess(`${setting.category}::${setting.key}`)
      setEditingKey(null)
      fetchSettings()
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Failed to save'
      alert(message)
    } finally {
      setSaving(false)
    }
  }

  const handleCancel = () => {
    setEditingKey(null)
    setEditValue('')
  }

  if (loading) return <LoadingState />
  if (error) return <ErrorState message={error} onRetry={fetchSettings} />

  // Group settings by category
  const grouped = settings.reduce<Record<string, SystemSetting[]>>((acc, s) => {
    if (!acc[s.category]) acc[s.category] = []
    acc[s.category].push(s)
    return acc
  }, {})

  return (
    <div className="space-y-6">
      {Object.keys(grouped).length === 0 ? (
        <EmptyState message="No settings configured yet." />
      ) : (
        Object.entries(grouped).map(([category, items]) => (
          <div
            key={category}
            className="rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800"
          >
            <div className="border-b border-gray-200 dark:border-gray-700 px-4 py-3">
              <h3 className="text-sm font-semibold text-gray-900 dark:text-white capitalize">
                {category.replace(/_/g, ' ')}
              </h3>
            </div>
            <div className="divide-y divide-gray-100 dark:divide-gray-700">
              {items.map((setting) => {
                const compositeKey = `${setting.category}::${setting.key}`
                const isEditing = editingKey === compositeKey
                const justSaved = saveSuccess === compositeKey

                return (
                  <div key={compositeKey} className="px-4 py-3">
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-gray-900 dark:text-white">
                          {setting.key.replace(/_/g, ' ')}
                        </p>
                        {setting.description && (
                          <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                            {setting.description}
                          </p>
                        )}
                        {!isEditing && (
                          <p className="mt-1 text-sm text-gray-600 dark:text-gray-300 font-mono break-all">
                            {typeof setting.value === 'string'
                              ? setting.value
                              : JSON.stringify(setting.value)}
                          </p>
                        )}
                      </div>
                      {!isEditing && (
                        <div className="flex items-center gap-2">
                          {justSaved && (
                            <CheckCircle className="h-4 w-4 text-green-500" />
                          )}
                          <button
                            onClick={() => handleEdit(setting)}
                            className="text-xs px-2 py-1 rounded bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
                          >
                            Edit
                          </button>
                        </div>
                      )}
                    </div>
                    {isEditing && (
                      <div className="mt-2 space-y-2">
                        <textarea
                          className="w-full text-sm border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-white font-mono focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                          rows={3}
                          value={editValue}
                          onChange={(e) => setEditValue(e.target.value)}
                          disabled={saving}
                        />
                        <div className="flex gap-2">
                          <button
                            onClick={() => handleSave(setting)}
                            disabled={saving}
                            className="inline-flex items-center gap-1 text-xs px-3 py-1.5 rounded bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50 transition-colors"
                          >
                            {saving ? <Loader2 className="h-3 w-3 animate-spin" /> : <Save className="h-3 w-3" />}
                            Save
                          </button>
                          <button
                            onClick={handleCancel}
                            disabled={saving}
                            className="text-xs px-3 py-1.5 rounded bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
                          >
                            Cancel
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                )
              })}
            </div>
          </div>
        ))
      )}
    </div>
  )
}

// ─── Cron Jobs Tab ─────────────────────────────────────────────────────────────

function CronJobsTab() {
  const [jobs, setJobs] = useState<CronJob[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [triggeringId, setTriggeringId] = useState<string | null>(null)
  const [triggerSuccess, setTriggerSuccess] = useState<string | null>(null)

  const fetchJobs = useCallback(async () => {
    try {
      setLoading(true)
      setError(null)
      const res = await adminService.getCronJobs()
      const data = res.data?.jobs || res.data || []
      setJobs(Array.isArray(data) ? data : [])
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to load cron jobs')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => { fetchJobs() }, [fetchJobs])

  const handleTrigger = async (jobId: string) => {
    try {
      setTriggeringId(jobId)
      setTriggerSuccess(null)
      await adminService.triggerCronJob(jobId)
      setTriggerSuccess(jobId)
      setTimeout(() => setTriggerSuccess(null), 3000)
      fetchJobs()
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Failed to trigger job'
      alert(message)
    } finally {
      setTriggeringId(null)
    }
  }

  if (loading) return <LoadingState />
  if (error) return <ErrorState message={error} onRetry={fetchJobs} />

  return (
    <div className="rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 overflow-hidden">
      {jobs.length === 0 ? (
        <EmptyState message="No cron jobs registered." />
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50">
                <th className="text-left px-4 py-3 font-medium text-gray-700 dark:text-gray-300">Name</th>
                <th className="text-left px-4 py-3 font-medium text-gray-700 dark:text-gray-300">Schedule</th>
                <th className="text-left px-4 py-3 font-medium text-gray-700 dark:text-gray-300">Status</th>
                <th className="text-left px-4 py-3 font-medium text-gray-700 dark:text-gray-300">Last Run</th>
                <th className="text-left px-4 py-3 font-medium text-gray-700 dark:text-gray-300">Next Run</th>
                <th className="text-right px-4 py-3 font-medium text-gray-700 dark:text-gray-300">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
              {jobs.map((job) => (
                <tr key={job.id} className="hover:bg-gray-50 dark:hover:bg-gray-700/30">
                  <td className="px-4 py-3">
                    <div>
                      <p className="font-medium text-gray-900 dark:text-white">{job.name}</p>
                      {job.description && (
                        <p className="text-xs text-gray-500 dark:text-gray-400">{job.description}</p>
                      )}
                    </div>
                  </td>
                  <td className="px-4 py-3 font-mono text-xs text-gray-600 dark:text-gray-400">
                    {job.cronExpression}
                  </td>
                  <td className="px-4 py-3">
                    <StatusBadge status={job.status} />
                    {job.status === 'failed' && job.lastError && (
                      <p className="mt-1 text-xs text-red-600 dark:text-red-400 max-w-[200px] truncate" title={job.lastError}>
                        {job.lastError}
                      </p>
                    )}
                  </td>
                  <td className="px-4 py-3 text-xs text-gray-600 dark:text-gray-400">
                    {job.lastRunAt ? formatDate(job.lastRunAt) : '—'}
                  </td>
                  <td className="px-4 py-3 text-xs text-gray-600 dark:text-gray-400">
                    {job.nextRunAt ? formatDate(job.nextRunAt) : '—'}
                  </td>
                  <td className="px-4 py-3 text-right">
                    <button
                      onClick={() => handleTrigger(job.id)}
                      disabled={triggeringId === job.id}
                      className="inline-flex items-center gap-1 text-xs px-2.5 py-1.5 rounded bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50 transition-colors"
                    >
                      {triggeringId === job.id ? (
                        <Loader2 className="h-3 w-3 animate-spin" />
                      ) : triggerSuccess === job.id ? (
                        <CheckCircle className="h-3 w-3" />
                      ) : (
                        <Play className="h-3 w-3" />
                      )}
                      {triggerSuccess === job.id ? 'Triggered' : 'Run Now'}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}

// ─── Feature Flags Tab ─────────────────────────────────────────────────────────

function FeatureFlagsTab() {
  const [flags, setFlags] = useState<FeatureFlag[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [togglingId, setTogglingId] = useState<string | null>(null)

  const fetchFlags = useCallback(async () => {
    try {
      setLoading(true)
      setError(null)
      const res = await adminService.listFeatureFlags()
      const data = res.data?.flags || res.data || []
      setFlags(Array.isArray(data) ? data : [])
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to load feature flags')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => { fetchFlags() }, [fetchFlags])

  const handleToggle = async (flag: FeatureFlag) => {
    try {
      setTogglingId(flag.id)
      await adminService.toggleFeatureFlag(flag.id, !flag.enabled)
      setFlags((prev) =>
        prev.map((f) => (f.id === flag.id ? { ...f, enabled: !f.enabled } : f))
      )
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Failed to toggle flag'
      alert(message)
    } finally {
      setTogglingId(null)
    }
  }

  if (loading) return <LoadingState />
  if (error) return <ErrorState message={error} onRetry={fetchFlags} />

  return (
    <div className="space-y-4">
      {flags.length === 0 ? (
        <EmptyState message="No feature flags configured." />
      ) : (
        <div className="rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 divide-y divide-gray-100 dark:divide-gray-700">
          {flags.map((flag) => (
            <div
              key={flag.id}
              className="flex items-center justify-between px-4 py-3"
            >
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-gray-900 dark:text-white">
                  {flag.name}
                </p>
                {flag.description && (
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                    {flag.description}
                  </p>
                )}
                {flag.targetRoles && flag.targetRoles.length > 0 && (
                  <div className="flex gap-1 mt-1 flex-wrap">
                    {flag.targetRoles.map((role) => (
                      <span
                        key={role}
                        className="inline-block text-[10px] px-1.5 py-0.5 rounded bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400"
                      >
                        {role}
                      </span>
                    ))}
                  </div>
                )}
              </div>
              <button
                onClick={() => handleToggle(flag)}
                disabled={togglingId === flag.id}
                className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 dark:focus:ring-offset-gray-800 disabled:opacity-50 ${
                  flag.enabled ? 'bg-blue-600' : 'bg-gray-200 dark:bg-gray-600'
                }`}
                role="switch"
                aria-checked={flag.enabled}
                aria-label={`Toggle ${flag.name}`}
              >
                <span
                  className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                    flag.enabled ? 'translate-x-5' : 'translate-x-0'
                  }`}
                />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

// ─── Scoring Rules Tab ─────────────────────────────────────────────────────────

function ScoringRulesTab() {
  const [rules, setRules] = useState<string>('')
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [saveSuccess, setSaveSuccess] = useState(false)

  const fetchRules = useCallback(async () => {
    try {
      setLoading(true)
      setError(null)
      const res = await adminService.getSettings()
      const data = res.data?.settings || res.data || []
      const settingsArr = Array.isArray(data) ? data : []
      const scoringSetting = settingsArr.find(
        (s: SystemSetting) => s.category === 'scoring' && s.key === 'rules'
      )
      setRules(
        scoringSetting
          ? typeof scoringSetting.value === 'string'
            ? scoringSetting.value
            : JSON.stringify(scoringSetting.value, null, 2)
          : JSON.stringify(
              {
                refreshFrequency: 'daily',
                recalculationTriggers: ['data_update', 'manual', 'schedule'],
                weights: {
                  financial: 0.3,
                  compliance: 0.25,
                  operational: 0.25,
                  reputation: 0.2,
                },
              },
              null,
              2
            )
      )
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to load scoring rules')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => { fetchRules() }, [fetchRules])

  const handleSave = async () => {
    try {
      // Validate JSON
      JSON.parse(rules)
    } catch {
      alert('Invalid JSON. Please fix the syntax before saving.')
      return
    }

    try {
      setSaving(true)
      setSaveSuccess(false)
      await adminService.updateSetting('scoring', 'rules', JSON.parse(rules))
      setSaveSuccess(true)
      setTimeout(() => setSaveSuccess(false), 3000)
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Failed to save scoring rules'
      alert(message)
    } finally {
      setSaving(false)
    }
  }

  if (loading) return <LoadingState />
  if (error) return <ErrorState message={error} onRetry={fetchRules} />

  return (
    <div className="rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-4 space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-sm font-semibold text-gray-900 dark:text-white">Scoring Rules</h3>
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
            Configure score calculation rules including refresh frequency, recalculation triggers, and dimension weights.
          </p>
        </div>
        <button
          onClick={handleSave}
          disabled={saving}
          className="inline-flex items-center gap-1.5 text-sm px-3 py-1.5 rounded-md bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50 transition-colors"
        >
          {saving ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : saveSuccess ? (
            <CheckCircle className="h-4 w-4" />
          ) : (
            <Save className="h-4 w-4" />
          )}
          {saveSuccess ? 'Saved' : 'Save Rules'}
        </button>
      </div>
      <textarea
        className="w-full h-64 text-sm font-mono border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-y"
        value={rules}
        onChange={(e) => setRules(e.target.value)}
        placeholder="Enter scoring rules as JSON..."
        spellCheck={false}
      />
    </div>
  )
}

// ─── Notifications Tab ─────────────────────────────────────────────────────────

function NotificationsTab() {
  const [rules, setRules] = useState<string>('')
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [saveSuccess, setSaveSuccess] = useState(false)

  const fetchRules = useCallback(async () => {
    try {
      setLoading(true)
      setError(null)
      const res = await adminService.getSettings()
      const data = res.data?.settings || res.data || []
      const settingsArr = Array.isArray(data) ? data : []
      const notifSetting = settingsArr.find(
        (s: SystemSetting) => s.category === 'notifications' && s.key === 'rules'
      )
      setRules(
        notifSetting
          ? typeof notifSetting.value === 'string'
            ? notifSetting.value
            : JSON.stringify(notifSetting.value, null, 2)
          : JSON.stringify(
              {
                events: {
                  score_change: { email: true, push: true },
                  badge_earned: { email: true, push: true },
                  subscription_expiry: { email: true, push: false },
                  system_alert: { email: true, push: true },
                },
                quietHours: { enabled: false, start: '22:00', end: '08:00' },
              },
              null,
              2
            )
      )
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to load notification rules')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => { fetchRules() }, [fetchRules])

  const handleSave = async () => {
    try {
      JSON.parse(rules)
    } catch {
      alert('Invalid JSON. Please fix the syntax before saving.')
      return
    }

    try {
      setSaving(true)
      setSaveSuccess(false)
      await adminService.updateSetting('notifications', 'rules', JSON.parse(rules))
      setSaveSuccess(true)
      setTimeout(() => setSaveSuccess(false), 3000)
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Failed to save notification rules'
      alert(message)
    } finally {
      setSaving(false)
    }
  }

  if (loading) return <LoadingState />
  if (error) return <ErrorState message={error} onRetry={fetchRules} />

  return (
    <div className="rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-4 space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-sm font-semibold text-gray-900 dark:text-white">Notification Rules</h3>
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
            Configure which email and push notifications are sent to users for specified event types.
          </p>
        </div>
        <button
          onClick={handleSave}
          disabled={saving}
          className="inline-flex items-center gap-1.5 text-sm px-3 py-1.5 rounded-md bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50 transition-colors"
        >
          {saving ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : saveSuccess ? (
            <CheckCircle className="h-4 w-4" />
          ) : (
            <Save className="h-4 w-4" />
          )}
          {saveSuccess ? 'Saved' : 'Save Rules'}
        </button>
      </div>
      <textarea
        className="w-full h-64 text-sm font-mono border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-y"
        value={rules}
        onChange={(e) => setRules(e.target.value)}
        placeholder="Enter notification rules as JSON..."
        spellCheck={false}
      />
    </div>
  )
}

// ─── AI Config Tab ─────────────────────────────────────────────────────────────

function AIConfigTab() {
  const [config, setConfig] = useState<string>('')
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [saveSuccess, setSaveSuccess] = useState(false)

  const fetchConfig = useCallback(async () => {
    try {
      setLoading(true)
      setError(null)
      const res = await adminService.getSettings()
      const data = res.data?.settings || res.data || []
      const settingsArr = Array.isArray(data) ? data : []
      const aiSetting = settingsArr.find(
        (s: SystemSetting) => s.category === 'ai' && s.key === 'model_settings'
      )
      setConfig(
        aiSetting
          ? typeof aiSetting.value === 'string'
            ? aiSetting.value
            : JSON.stringify(aiSetting.value, null, 2)
          : JSON.stringify(
              {
                demoMode: false,
                provider: 'openai',
                model: 'gpt-4',
                fallbackBehavior: 'use_cached',
                apiEndpoint: '',
                maxTokens: 4096,
                temperature: 0.7,
                retryAttempts: 3,
              },
              null,
              2
            )
      )
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to load AI configuration')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => { fetchConfig() }, [fetchConfig])

  const handleSave = async () => {
    try {
      JSON.parse(config)
    } catch {
      alert('Invalid JSON. Please fix the syntax before saving.')
      return
    }

    try {
      setSaving(true)
      setSaveSuccess(false)
      await adminService.updateSetting('ai', 'model_settings', JSON.parse(config))
      setSaveSuccess(true)
      setTimeout(() => setSaveSuccess(false), 3000)
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Failed to save AI configuration'
      alert(message)
    } finally {
      setSaving(false)
    }
  }

  if (loading) return <LoadingState />
  if (error) return <ErrorState message={error} onRetry={fetchConfig} />

  return (
    <div className="rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-4 space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-sm font-semibold text-gray-900 dark:text-white">AI Model Settings</h3>
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
            Configure AI provider, demo mode, API credentials, and fallback behavior.
          </p>
        </div>
        <button
          onClick={handleSave}
          disabled={saving}
          className="inline-flex items-center gap-1.5 text-sm px-3 py-1.5 rounded-md bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50 transition-colors"
        >
          {saving ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : saveSuccess ? (
            <CheckCircle className="h-4 w-4" />
          ) : (
            <Save className="h-4 w-4" />
          )}
          {saveSuccess ? 'Saved' : 'Save Config'}
        </button>
      </div>
      <textarea
        className="w-full h-64 text-sm font-mono border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-y"
        value={config}
        onChange={(e) => setConfig(e.target.value)}
        placeholder="Enter AI model configuration as JSON..."
        spellCheck={false}
      />
    </div>
  )
}

// ─── Shared Components ─────────────────────────────────────────────────────────

function StatusBadge({ status }: { status: 'running' | 'idle' | 'failed' }) {
  const styles = {
    running: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400',
    idle: 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300',
    failed: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400',
  }

  const icons = {
    running: <CheckCircle className="h-3 w-3" />,
    idle: <Clock className="h-3 w-3" />,
    failed: <XCircle className="h-3 w-3" />,
  }

  return (
    <span className={`inline-flex items-center gap-1 text-xs font-medium px-2 py-0.5 rounded-full ${styles[status]}`}>
      {icons[status]}
      {status.charAt(0).toUpperCase() + status.slice(1)}
    </span>
  )
}

function LoadingState() {
  return (
    <div className="flex items-center justify-center py-12">
      <Loader2 className="h-6 w-6 animate-spin text-blue-600 dark:text-blue-400" />
      <span className="ml-2 text-sm text-gray-600 dark:text-gray-400">Loading...</span>
    </div>
  )
}

function ErrorState({ message, onRetry }: { message: string; onRetry: () => void }) {
  return (
    <div className="flex flex-col items-center justify-center py-12">
      <AlertTriangle className="h-8 w-8 text-red-500 mb-2" />
      <p className="text-sm text-red-600 dark:text-red-400 mb-3">{message}</p>
      <button
        onClick={onRetry}
        className="inline-flex items-center gap-1.5 text-sm px-3 py-1.5 rounded-md bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
      >
        <RefreshCw className="h-4 w-4" />
        Retry
      </button>
    </div>
  )
}

function EmptyState({ message }: { message: string }) {
  return (
    <div className="flex flex-col items-center justify-center py-12">
      <Settings className="h-8 w-8 text-gray-400 dark:text-gray-500 mb-2" />
      <p className="text-sm text-gray-500 dark:text-gray-400">{message}</p>
    </div>
  )
}

// ─── Helpers ───────────────────────────────────────────────────────────────────

function formatDate(dateStr: string): string {
  try {
    const date = new Date(dateStr)
    return date.toLocaleString(undefined, {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    })
  } catch {
    return dateStr
  }
}
